import * as React from 'react';

import {useLazyQuery} from '@apollo/react-hooks';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {TickCircleIcon} from 'shells/icon/svg/tick-circle-icon';
import {Divider} from 'shells/layout/divider';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Sidebar} from 'shells/sidebar/sidebar';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {IsNumberVerified, IsNumberVerifiedVariables} from 'nutshell-graphql-types';

import {useVerifyPhoneNumber} from '../graphql/hooks';
import IS_NUMBER_VERIFIED from '../graphql/queries/is-number-verified.graphql';

import styles from './redirect-phone-sidebar-form.module.css';

type Props = {
    onClose: () => void;
    initialPhoneNumber: string | null | undefined;
    onDelete: () => Promise<any>;
    onSave: (phoneNumber?: string | null | undefined) => Promise<any>;
    isSaving: boolean;
    saveFailedErrorMessage: string | null | undefined;
};

export function RedirectSidebarForm(props: Props) {
    const [verificationCode, setVerificationCode] = React.useState<any>(undefined);
    const [errorMessage, setErrorMessage] = React.useState<any>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

    const {verifyPhoneNumber} = useVerifyPhoneNumber();
    const [checkIsNumberVerified, {refetch, called, data}] = useLazyQuery<
        IsNumberVerified,
        IsNumberVerifiedVariables
    >(IS_NUMBER_VERIFIED);

    const initialPhoneNumber = props.initialPhoneNumber || '';
    const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);
    const [isFetchingVerificationCode, setIsFetchingVerificationCode] = React.useState(false);
    const [isNumberVerified, setIsNumberVerified] = React.useState(false);

    const [tempErrorMessage, setTempErrorMessage] = React.useState<any>(null);

    React.useEffect(() => {
        if (!props.saveFailedErrorMessage) {
            return;
        }

        setTempErrorMessage(props.saveFailedErrorMessage);

        const id = setTimeout(() => {
            setTempErrorMessage(null);
        }, 5000);

        return () => clearTimeout(id);
    }, [props.saveFailedErrorMessage]);

    React.useEffect(() => {
        if (data && data.isNumberVerified) {
            setIsNumberVerified(data.isNumberVerified);
            setVerificationCode(undefined);
        }
    }, [data, setIsNumberVerified, setVerificationCode]);

    React.useEffect(() => {
        if (!verificationCode || !phoneNumber || isNumberVerified) {
            return;
        }

        const start = Date.now();
        const id = setInterval(() => {
            if (Date.now() - start > 60000) {
                setVerificationCode(undefined);

                return;
            }

            refetch({phoneNumber}).then((response) => {
                if (response && response.data && response.data.isNumberVerified) {
                    setIsNumberVerified(response.data.isNumberVerified);
                    setVerificationCode(undefined);
                    clearInterval(id);
                }
            });
        }, 5000);

        return () => clearInterval(id);
    }, [verificationCode, phoneNumber, isNumberVerified, refetch]);

    const verifyPhone = () => {
        if (!phoneNumber.trim()) {
            setErrorMessage('Please enter a phone number');

            return;
        }

        if (isFetchingVerificationCode || !phoneNumber || isNumberVerified) {
            return;
        }

        setIsFetchingVerificationCode(true);

        if (!called) {
            checkIsNumberVerified({variables: {phoneNumber}});
        }

        verifyPhoneNumber(phoneNumber)
            .then((response) => {
                setIsFetchingVerificationCode(false);
                if (!response.data || !response.data.phoneNumberVerify.verificationCode) {
                    return;
                }

                setVerificationCode(response.data.phoneNumberVerify.verificationCode);
            })
            .catch((response) => {
                setIsFetchingVerificationCode(false);

                if (!response.graphQLErrors || response.graphQLErrors < 1) {
                    setErrorMessage('An error occurred');

                    return;
                }

                setErrorMessage(response.graphQLErrors[0].message);
            });
    };

    // @ts-expect-error
    const handlePhoneNumberChange = (newValue) => {
        setPhoneNumber(newValue);
        setVerificationCode(undefined);
        setIsNumberVerified(false);
        setErrorMessage(undefined);
    };

    let verificationComponent;

    if (isNumberVerified) {
        verificationComponent = (
            <div className='flex flex-dir-row gap-8 align-center'>
                <TickCircleIcon size={16} wrapWithDiv={false} color={colors.lead} />
                <Body>Your phone number has been verified</Body>
            </div>
        );
    } else if (verificationCode) {
        verificationComponent = (
            <div className='flex flex-dir-col'>
                <Body>When prompted during the call, please enter</Body>
                <Body size='xlarge' isBold={true}>
                    {verificationCode}
                </Body>
                <Body>to verify your number.</Body>
            </div>
        );
    } else {
        verificationComponent = (
            <StackLegacy spacing={8}>
                <Body>
                    We’ll call your phone number and provide a verification code to enter during the
                    call.
                </Body>
                <Button
                    disabled={!phoneNumber.trim()}
                    variant='primary'
                    size='big'
                    onClick={verifyPhone}
                >
                    Call me
                </Button>
            </StackLegacy>
        );
    }

    const shownErrorMessage = tempErrorMessage || errorMessage;

    return (
        <Sidebar
            isOpen={true}
            onClose={props.onClose}
            headerTitle='Redirect phone number'
            onSave={() => props.onSave(phoneNumber)}
            isSaving={props.isSaving}
            submitDisabled={!isNumberVerified}
        >
            <div className={styles['redirect-phone-form']}>
                <div>
                    {shownErrorMessage && (
                        <FlashBanner type='error' leftAlignContent={true}>
                            {shownErrorMessage}
                        </FlashBanner>
                    )}
                    <Body>
                        When a phone number tries to call your company phone number, the phone call
                        with redirect to this number
                    </Body>
                    <Divider spacing={16} variant='line--horizontal' />
                    <StackLegacy spacing={8}>
                        <Body>Enter redirect number</Body>
                        <TextField
                            placeholder={'(999) 999-9999'}
                            onChange={handlePhoneNumberChange}
                            value={phoneNumber}
                        />
                        <></>
                    </StackLegacy>
                    {verificationComponent}
                </div>

                {initialPhoneNumber && (
                    <div className='full-width flex justify-end border-box'>
                        <Button
                            variant='danger'
                            size='big'
                            isFullWidth={true}
                            onClick={() => {
                                setShowDeleteConfirmation(true);
                            }}
                        >
                            Remove redirect number
                        </Button>
                    </div>
                )}
            </div>
            {showDeleteConfirmation && (
                <ConfirmationDialog
                    title='Delete redirect phone number'
                    message='Are you sure you want to delete this redirect phone number?'
                    confirmText='Delete'
                    cancelText='Cancel'
                    onCancel={() => {
                        setShowDeleteConfirmation(false);
                    }}
                    onConfirm={() => {
                        props.onDelete();
                    }}
                />
            )}
        </Sidebar>
    );
}
